import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";

import ControlledPhoneInputSeparatedLabel from "../../../../../../components/controlled/ControlledPhoneInputSeparatedLabel";
import ControlledRadioGroupSeparatedLabel from "../../../../../../components/controlled/ControlledRadioGroupSeparatedLabel";
import ControlledSelectSeparatedLabel from "../../../../../../components/controlled/ControlledSelectSeparatedLabel";
import ControlledTextFieldSeparatedLabel from "../../../../../../components/controlled/ControlledTextFieldSeparatedLabel";

import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import EditUserSkeleton from "../../../../../../components/skeletons/EditUserSkeleton";
import { GET_USER_ROLES_OPTIONS } from "../../../../../../constants";
import { useGetCompanyWithIdBranchesQuery } from "../../../../../branches/branchesApiSlice";
import {
	useGetUserWithIdQuery,
	useUpdateUserWithIdMutation,
} from "../../../../../user/userApiSlice";

const EditDialog = ({ onClose, open, skip, userId, companyId }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const { data: user, isLoading: isLoadingUser } = useGetUserWithIdQuery(
		userId,
		{
			skip,
		}
	);
	const { data: branches, isLoading: isLoadingBranches } =
		useGetCompanyWithIdBranchesQuery(
			{ companyId },
			{
				skip,
			}
		);

	const [editUser, { isLoading: isUpdating }] = useUpdateUserWithIdMutation();

	const {
		handleSubmit,
		control,
		formState: { errors },
		setValue,
		setError,
	} = useForm({
		// resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	const handleEditUser = async ({
		firstName,
		lastName,
		email,
		phone,
		branch,
		role,
	}) => {
		await editUser({
			userId,
			data: { firstName, lastName, email, phone, branch, role },
		})
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("updated_user"), {
					variant: "success",
				});
				onClose();
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	useEffect(() => {
		if (user) {
			setTimeout(() => {
				setValue("firstName", user.first_name);
				setValue("lastName", user.last_name);
				setValue("email", user.email);
				setValue("phone", user.phone_number);
				setValue("branch", user.company_branch_id);
				setValue("role", user.role);
			}, 200);
		}
	}, [user, setValue, open]);

	return (
		<Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
			<Box sx={{ padding: "30px" }}>
				{(!isLoadingUser || !isLoadingBranches) && branches && user ? (
					<Box component="form" onSubmit={handleSubmit(handleEditUser)}>
						<Grid container spacing={1.5}>
							<Grid size={12}>
								<ControlledTextFieldSeparatedLabel
									control={control}
									name="firstName"
									label={t("first_name_label")}
									error={!!errors?.firstName}
									required
								/>
							</Grid>
							<Grid size={12}>
								<ControlledTextFieldSeparatedLabel
									control={control}
									name="lastName"
									label={t("surname_label")}
									error={!!errors?.lastName}
									required
								/>
							</Grid>
							<Grid size={12}>
								<ControlledTextFieldSeparatedLabel
									control={control}
									name="email"
									type="email"
									label={t("email_and_login_label")}
									error={!!errors?.customerPrice}
									required
								/>
							</Grid>
							<Grid size={12}>
								<ControlledPhoneInputSeparatedLabel
									control={control}
									name="phone"
									type="tel"
									label={t("phone_label")}
									error={!!errors?.phone}
									required
								/>
							</Grid>
							<Grid size={12}>
								<ControlledSelectSeparatedLabel
									control={control}
									name="branch"
									label={t("branch")}
									defaultValue=""
									options={branches.map((branch) => {
										return { value: branch.id, label: branch.name };
									})}
								/>
							</Grid>
							<Grid size={12}>
								<ControlledRadioGroupSeparatedLabel
									className="user-role-radio-group"
									control={control}
									name="role"
									label={t("permission_type")}
									defaultValue=""
									options={GET_USER_ROLES_OPTIONS(t)}
								/>
							</Grid>
							<Grid size={12}>
								<Box sx={{ display: "flex", justifyContent: "center" }}>
									<Button
										loading={isUpdating}
										type="submit"
										variant="contained"
									>
										{t("save")}
									</Button>
								</Box>
							</Grid>
						</Grid>
					</Box>
				) : (
					<EditUserSkeleton />
				)}
			</Box>
		</Dialog>
	);
};

export default EditDialog;
